import autoBind from 'auto-bind'
import {IncomingHttpHeaders} from 'http2'
import path from 'path'
import Action, {actionToJson} from 'src/models/Action'
import ActionType from 'src/models/ActionType'
import Activity from 'src/models/Activity'
import BodyArea from 'src/models/BodyArea'
import Equipment from 'src/models/Equipment'
import Goal from 'src/models/Goal'
import Routine, {routineToJson} from 'src/models/Routine'
import User, {userToJson} from 'src/models/User'
import environment, {Environment} from '../environment'
import Api from './Api'

/**
 * Returns the URL to use to fetch a piece of content from the API. Note that
 * this doesn't make the call, it merely returns the URL.
 *
 * @param contentName - the name of the content for which we want the URL
 */
export const contentURL = (contentName: string): string => {
  return `${environment.apiUrl}/content/${contentName}`
}

export default class ReactPTApi {
  currentEnvironment: Environment
  api: Api

  /**
   * Creates a new instance of the react pt api
   * @param currentEnvironment the api Environment to use
   */
  constructor(currentEnvironment: Environment) {
    autoBind(this)
    this.currentEnvironment = environment
    const headers: IncomingHttpHeaders = {
      'Content-Type': 'application/json',
    }
    this.api = new Api(this.currentEnvironment.apiUrl, headers)
  }

  /**
   * Sets the token to use for api calls
   * @param token the user token to use
   */
  setToken(token?: string) {
    this.api.headers.Authorization = token
  }

  /**
   * Logs in using the email and password provided
   * @param email the user's email address
   * @param password the user's password
   */
  async login(email: string, password: string) {
    try {
      const user = await this.api.postJson('/login', {email, password})
      this.setToken(user.token)
      return user as User
    } catch (error) {
      if (error.originalResponse.status === 401) {
        throw new Error('Invalid email or password.')
      } else {
        throw error
      }
    }
  }

  /**
   * Fetches all actions
   */
  async getAllActions() {
    const result = await this.api.get('/action')
    return result as Action[]
  }
  /**
   * Fetches an action
   * @param id the id of the action
   */
  async getAction(id: string) {
    const result = await this.api.get(`/action/${id}`)
    return result as Action
  }

  /**
   * Creates an action
   * @param action the action to create
   */
  async createAction(action: Action) {
    const result = await this.api.postJson(`/action`, actionToJson(action))
    return result as Action
  }

  /**
   * Updates an action
   * @param action the action to create
   */
  async updateAction(action: Action) {
    const result = await this.api.putJson(`/action/${action.id}`, actionToJson(action))
    return result as Action
  }

  /**
   * Deletes an action
   * @param actionId the action to delete
   */
  async deleteAction(actionId: string) {
    await this.api.delete(`/action/${actionId}`)
  }

  /**
   * Fetches all actions
   */
  async getActionTypes() {
    const result = await this.api.get('/actiontype')
    return result as ActionType[]
  }

  /**
   * Fetches all activities
   */
  async getActivities() {
    const result = await this.api.get('/activity')
    return result as Activity[]
  }

  /**
   * Fetches all goals
   */
  async getGoals() {
    const result = await this.api.get('/goal')
    return result as Goal[]
  }

  /**
   * Fetches all actions
   */
  async getEquipment() {
    const result = await this.api.get('/equipment')
    return result as Equipment[]
  }

  /**
   * Fetches all actions
   */
  async getBodyAreas() {
    const result = await this.api.get('/bodyarea')
    return result as BodyArea[]
  }

  /**
   * Requests an upload URL for a video.
   */
  async requestVideoUpload() {
    const {uploadURL, id} = await this.api.get('/video_upload_request')
    return {uploadURL, id}
  }

  /**
   * Requests the info for an uploaded video
   * @param uploadID the ID of the video upload (from requestUpload)
   */
  async requestVideoUploadInfo(uploadID: string) {
    const {videoUrl, thumbnailUrl} = await this.api.get(`/video_upload_request/${uploadID}`)
    return {videoUrl, thumbnailUrl}
  }

  /**
   * Uploads a local video file to the api
   * @param uploadURL URL to which we upload the video.
   * @param videoFile the file path to the video
   */
  async uploadVideo(uploadURL: string, videoFile: File) {
    // We need a temp API to make calls to mux.
    let muxAPI = new Api('', {'Content-Type': 'video/mp4'})
    return await this.uploadFileWithAPI(muxAPI, uploadURL, videoFile)
  }

  /**
   * Requests an upload URL for an image.
   * @param imageFile - the file that is being uploaded
   */
  async requestImageUpload(imageFile: File) {
    // Need just the file name (minus the path)
    const imageFileName = path.basename(imageFile.name)

    const {uploadURL, contentName} = await this.api.get(`/image_upload_request/${imageFileName}`)
    return {uploadURL, contentName}
  }

  /**
   * Requests the info for an uploaded image
   * @param contentName the name of the image uploaded (from requestUpload)
   */
  async requestImageUploadInfo(contentName: string) {
    const {imageUrl} = await this.api.get(`/image_upload_request/${contentName}`)
    return {imageUrl}
  }

  /**
   * Uploads a local image file to the api
   * @param uploadURL URL to which we upload the image.
   * @param imageFile the file path to the image
   */
  async uploadImage(uploadURL: string, imageFile: File) {
    // We need a temp API to make calls to upload.
    let api = new Api('', {'Content-Type': 'image/png'})
    return await this.uploadFileWithAPI(api, uploadURL, imageFile)
  }

  /**
   * Uploads a local file file to given API. The reason this is broken out
   * (from the `uploadImage/Video` above) is so that it may be tested with a
   * mocked API.
   * @param uploadAPI A standalone API to use for upload calls..
   * @param uploadURL URL to which we upload the file.
   * @param file the file path that which should be uploaded
   */
  async uploadFileWithAPI(uploadAPI: Api, uploadURL: string, file: File) {
    // Get all the data to be uploaded (the actual file).
    const data = await file.arrayBuffer()

    // Ship it!
    return await uploadAPI.put(uploadURL, data)
  }

  /**
   * Fetches all users
   */
  async getAllUsers() {
    const result = await this.api.get('/user')
    return result as User[]
  }

  /**
   * Creates a user
   * @param user the action to create
   */
  async createUser(user: User) {
    const result = await this.api.postJson(`/user`, userToJson(user))
    return result as User
  }

  /**
   * Updates a user
   * @param user the user to create
   */
  async updateUser(user: User) {
    const result = await this.api.putJson(`/user/${user.id}`, userToJson(user))
    return result as User
  }

  /**
   * Gets all the routines from the API.
   */
  async getAllRoutines() {
    const result = await this.api.get('/routine?iup=true')
    return result as Routine[]
  }

  /**
   * Creates a public routine
   * @param routine the routine to create
   */
  async createPublicRoutine(routine: Routine) {
    const result = await this.api.postJson(`/routine/public`, routineToJson(routine))
    return result as Routine
  }

  /**
   * Updates a public routine
   * @param routine the routine to update
   */
  async updatePublicRoutine(routine: Routine) {
    const result = await this.api.putJson(`/routine/public/${routine.id}`, routineToJson(routine))
    return result as Routine
  }

  /**
   * Deletes a routine
   * @param routineId the routine to delete
   */
  async deleteRoutine(routineId: string) {
    await this.api.delete(`/routine/${routineId}`)
  }
}
